import { motion } from "framer-motion";
import { Landmark, Gavel, Check, FileDown, MessageCircle, AlertTriangle } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const PATHS = [
  {
    icon: Landmark,
    tag: "Via rápida",
    title: "Divórcio Extrajudicial",
    subtitle: "Feito em cartório, por escritura pública — sem processo na Justiça",
    featured: true,
    requirements: [
      {
        label: "Consenso entre as Partes:",
        text: "O casal deve estar em pleno acordo quanto ao fim da união, à divisão de bens (se houver), eventual pensão alimentícia e ao uso do sobrenome civil.",
      },
      {
        label: "Assistência Jurídica Obrigatória:",
        text: "É indispensável a participação de pelo menos um advogado. Um único advogado pode representar ambas as partes, ou cada cônjuge pode contratar o seu.",
      },
      {
        label: "Ausência de Filhos Menores de Idade ou Incapazes:",
        text: "Em regra, o casal não pode ter filhos menores de 18 anos ou incapazes.",
      },
      {
        label: "Ausência de Gravidez:",
        text: "A cônjuge não pode estar grávida (ou não ter conhecimento da gestação).",
      },
    ],
    note: "Mais rápido, mais barato e mais sigiloso — desde que a partilha esteja bem estruturada.",
  },
  {
    icon: Gavel,
    tag: "Via judicial",
    title: "Divórcio Judicial",
    subtitle: "Conduzido perante o juiz — consensual ou litigioso",
    featured: false,
    requirements: [
      "Obrigatório quando há filhos menores de 18 anos ou incapazes.",
      "Obrigatório para a mulher grávida.",
      "Necessário quando não há acordo sobre partilha, guarda ou pensão (litigioso).",
      "Consensual judicial: há acordo, mas a lei exige via judicial pelos filhos menores.",
      "Cada cônjuge deve ter seu próprio advogado em caso de litígio.",
    ],
    note: "Quando bem conduzido, o processo judicial protege seus direitos mesmo sem acordo da outra parte.",
  },
];

export const DivorceTypes = () => (
  <section id="modalidades" data-testid="divorce-types-section" className="relative py-24 sm:py-32 bg-[#0E1728]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Extrajudicial ou judicial"
        title="O caminho do divórcio: veja os requisitos"
        description="Entenda qual modalidade se aplica ao seu caso e quais requisitos a lei impõe. O checklist completo de documentos é enviado em PDF após o primeiro contato."
        align="center"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-14" data-testid="divorce-types-grid">
        {PATHS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1}>
            <motion.div
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
              data-testid={`divorce-type-card-${i}`}
              className={`h-full relative rounded-2xl p-8 sm:p-10 overflow-hidden transition-colors duration-500 ${
                p.featured
                  ? "border border-amber-400/35 bg-gradient-to-b from-[#162238] to-[#0F1830] shadow-[0_0_50px_rgba(212,175,55,0.08)]"
                  : "border border-amber-500/15 bg-[#111C2E] hover:border-amber-400/35"
              }`}
            >
              {p.featured && (
                <div className="absolute top-0 right-0 gold-surface text-[#0B1320] font-mono-num text-[10px] uppercase tracking-[0.2em] font-semibold px-4 py-1.5 rounded-bl-xl">
                  {p.tag}
                </div>
              )}
              {!p.featured && (
                <span className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 border border-slate-700 rounded-full px-3.5 py-1.5">
                  {p.tag}
                </span>
              )}

              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mt-6 ${p.featured ? "gold-surface" : "border border-amber-500/25 bg-[#0B1320]"}`}>
                <p.icon className={`w-6 h-6 ${p.featured ? "text-[#0B1320]" : "text-amber-400"}`} />
              </div>
              <h3 className="font-display text-2xl font-semibold text-slate-100 mt-6">{p.title}</h3>
              <p className="text-sm text-slate-400 mt-2">{p.subtitle}</p>

              <p className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-amber-400/90 mt-8 mb-4">
                Requisitos
              </p>
              <ul className="space-y-3">
                {p.requirements.map((r) =>
                  typeof r === "string" ? (
                    <li key={r} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                      <Check className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      {r}
                    </li>
                  ) : (
                    <li key={r.label} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                      <Check className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <span>
                        <span className="font-semibold text-slate-100">{r.label}</span> {r.text}
                      </span>
                    </li>
                  )
                )}
              </ul>

              <div className="flex items-start gap-3 border border-amber-500/20 bg-amber-400/5 rounded-xl px-4 py-3.5 mt-8" data-testid={`divorce-type-pdf-hint-${i}`}>
                <FileDown className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-100/80 leading-relaxed">
                  A lista de documentos desta modalidade é enviada em <span className="font-semibold">PDF personalizado</span> após o seu primeiro contato pelo WhatsApp.
                </p>
              </div>

              <p className="flex items-start gap-2.5 text-xs text-slate-500 leading-relaxed mt-6 pt-6 border-t border-amber-500/10">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500/70 mt-0.5 shrink-0" />
                {p.note}
              </p>
            </motion.div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-12 text-center">
        <p className="text-sm text-slate-500 mb-6 max-w-xl mx-auto">
          A documentação varia conforme o patrimônio do casal. Nós preparamos um checklist
          personalizado para o seu caso.
        </p>
        <motion.a
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          href={waLink(WA_MESSAGES.modalidades)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="divorce-types-cta-whatsapp"
          className="inline-flex items-center gap-2.5 gold-surface text-[#0B1320] font-bold text-sm sm:text-base px-8 py-4 rounded-full shadow-[0_0_36px_rgba(212,175,55,0.3)] hover:shadow-[0_0_55px_rgba(212,175,55,0.5)] transition-shadow duration-300"
        >
          <MessageCircle className="w-5 h-5" />
          Receber Meu Checklist de Documentos
        </motion.a>
      </Reveal>
    </div>
  </section>
);
